import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, Pause, Play, RefreshCw } from 'lucide-react';
import { videoAttendanceApi } from './videoAttendanceApi';

function todayYYYYMMDD() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function eventColor(eventType) {
  switch (eventType) {
    case 'ENTRY':
      return '#16a34a';
    case 'EXIT':
      return '#2563eb';
    case 'PPE_VIOLATION':
      return '#f97316';
    case 'RESTRICTED_AREA_VIOLATION':
      return '#ef4444';
    default:
      return '#6b7280';
  }
}

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function normalizeBBox(b) {
  if (!b || typeof b !== 'object') return null;
  const left = clamp01(Number(b.Left ?? b.left));
  const top = clamp01(Number(b.Top ?? b.top));
  const width = clamp01(Number(b.Width ?? b.width));
  const height = clamp01(Number(b.Height ?? b.height));
  if (![left, top, width, height].every(Number.isFinite)) return null;
  if (width <= 0 || height <= 0) return null;
  return { left, top, width, height };
}

function safeText(v) {
  return v == null ? '' : String(v);
}

const DEFAULT_FRAME_REFRESH_MS = 1500;
const DEFAULT_EVENTS_REFRESH_MS = 4000;

const LiveViewScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);

  const [siteId, setSiteId] = useState('');
  const [cameraId, setCameraId] = useState('');
  const [date, setDate] = useState(todayYYYYMMDD());

  const [frameUrl, setFrameUrl] = useState('');
  const [frameError, setFrameError] = useState('');
  const [frameLastUpdatedAt, setFrameLastUpdatedAt] = useState('');

  const [zones, setZones] = useState([]);
  const [events, setEvents] = useState([]);
  const [violations, setViolations] = useState([]);

  const [paused, setPaused] = useState(false);
  const [showZones, setShowZones] = useState(true);
  const [showEventsOverlay, setShowEventsOverlay] = useState(true);
  const [showViolationsOverlay, setShowViolationsOverlay] = useState(false);
  const [frameRefreshMs, setFrameRefreshMs] = useState(DEFAULT_FRAME_REFRESH_MS);
  const [eventsRefreshMs, setEventsRefreshMs] = useState(DEFAULT_EVENTS_REFRESH_MS);

  const [loadError, setLoadError] = useState('');
  const [resolvingViolationIds, setResolvingViolationIds] = useState(() => new Set());

  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const s = params.get('site_id') || params.get('siteId') || '';
    const c = params.get('camera_id') || params.get('cameraId') || '';
    const d = params.get('date') || '';
    setSiteId(String(s).trim());
    setCameraId(String(c).trim());
    if (d) setDate(String(d).trim());
  }, [params]);

  // Load zones (and refresh occasionally when camera changes).
  useEffect(() => {
    if (!cameraId) return;
    const ac = new AbortController();
    setLoadError('');
    (async () => {
      try {
        const { zones: z } = await videoAttendanceApi.getZoneConfig({ camera_id: cameraId, signal: ac.signal });
        if (!ac.signal.aborted) setZones(Array.isArray(z) ? z : []);
      } catch (e) {
        if (!ac.signal.aborted) {
          setZones([]);
          setLoadError(e?.message || 'Failed to load zone config.');
        }
      }
    })();
    return () => ac.abort();
  }, [cameraId]);

  const fetchFrame = async signal => {
    if (!cameraId) return;
    try {
      setFrameError('');
      const { url } = await videoAttendanceApi.getFrameUrl({ camera_id: cameraId, signal });
      if (!signal?.aborted) {
        setFrameUrl(url || '');
        setFrameLastUpdatedAt(new Date().toLocaleTimeString());
      }
    } catch (e) {
      if (!signal?.aborted) setFrameError(e?.message || 'Failed to refresh frame.');
    }
  };

  const fetchFeeds = async signal => {
    if (!siteId || !cameraId || !date) return;
    try {
      setLoadError('');
      const [ev, sv] = await Promise.all([
        videoAttendanceApi.getEvents({ site_id: siteId, date, camera_id: cameraId, limit: 50, signal }),
        videoAttendanceApi.getSafetyViolations({ site_id: siteId, date, camera_id: cameraId, status: 'open', signal }),
      ]);
      if (!signal?.aborted) {
        setEvents(Array.isArray(ev) ? ev : []);
        setViolations(Array.isArray(sv) ? sv : []);
      }
    } catch (e) {
      if (!signal?.aborted) setLoadError(e?.message || 'Failed to load live view feeds.');
    }
  };

  // Polling loops.
  useEffect(() => {
    if (!cameraId) return;
    if (paused) return;
    const ac = new AbortController();
    fetchFrame(ac.signal);
    const t = window.setInterval(() => fetchFrame(ac.signal), Math.max(500, Number(frameRefreshMs) || DEFAULT_FRAME_REFRESH_MS));
    return () => {
      ac.abort();
      window.clearInterval(t);
    };
  }, [cameraId, paused, frameRefreshMs]);

  useEffect(() => {
    if (!siteId || !cameraId) return;
    if (paused) return;
    const ac = new AbortController();
    fetchFeeds(ac.signal);
    const t = window.setInterval(
      () => fetchFeeds(ac.signal),
      Math.max(1000, Number(eventsRefreshMs) || DEFAULT_EVENTS_REFRESH_MS)
    );
    return () => {
      ac.abort();
      window.clearInterval(t);
    };
  }, [siteId, cameraId, date, paused, eventsRefreshMs]);

  const drawOverlays = () => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    const w = img.clientWidth;
    const h = img.clientHeight;
    if (!w || !h) return;

    // High-DPI support
    const dpr = window.devicePixelRatio || 1;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Zones
    if (showZones) {
      ctx.save();
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.fillStyle = 'rgba(56,189,248,0.10)';

      for (const z of zones) {
        const poly = Array.isArray(z?.polygon) ? z.polygon : [];
        if (poly.length < 2) continue;
        ctx.beginPath();
        poly.forEach((p, idx) => {
          const x = Math.round(clamp01(Number(p?.[0])) * w);
          const y = Math.round(clamp01(Number(p?.[1])) * h);
          if (idx === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        if (poly.length >= 3) ctx.closePath();
        ctx.fill();
        ctx.stroke();

        const label = safeText(z?.zone_label || z?.zone_id || '');
        if (label) {
          const p0 = poly[0];
          const x = Math.round(clamp01(Number(p0?.[0])) * w);
          const y = Math.round(clamp01(Number(p0?.[1])) * h);
          ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.fillRect(x + 6, y + 6, Math.min(220, ctx.measureText(label).width + 10), 18);
          ctx.fillStyle = 'rgba(255,255,255,0.95)';
          ctx.fillText(label, x + 10, y + 20);
          ctx.fillStyle = 'rgba(56,189,248,0.10)';
        }
      }
      ctx.restore();
    }

    // Events bboxes + labels
    if (showEventsOverlay) {
      ctx.save();
      ctx.lineWidth = 2;
      ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      for (const e of events.slice(0, 50)) {
        const bbox = normalizeBBox(e?.face_bbox);
        if (!bbox) continue;
        const x = Math.round(bbox.left * w);
        const y = Math.round(bbox.top * h);
        const bw = Math.round(bbox.width * w);
        const bh = Math.round(bbox.height * h);

        const color = eventColor(e?.event_type);
        ctx.strokeStyle = color;
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.strokeRect(x, y, bw, bh);

        const label = safeText(e?.event_type || 'EVENT');
        const textW = Math.min(240, ctx.measureText(label).width + 10);
        ctx.fillRect(x, Math.max(0, y - 18), textW, 18);
        ctx.fillStyle = 'white';
        ctx.fillText(label, x + 5, Math.max(12, y - 5));
      }
      ctx.restore();
    }

    // Optional: violation markers (simple corner mark)
    if (showViolationsOverlay) {
      ctx.save();
      ctx.fillStyle = 'rgba(239,68,68,0.9)';
      for (const sv of violations.slice(0, 50)) {
        const markerText = safeText(sv?.violation_type || 'VIOLATION');
        // If violations have bbox in future, place accordingly; for now render a small legend row.
        // (Keeping overlay optional to avoid misleading placement.)
        void markerText;
      }
      ctx.restore();
    }
  };

  // Redraw overlays when deps change or image resizes.
  useEffect(() => {
    drawOverlays();
  }, [zones, events, violations, showZones, showEventsOverlay, showViolationsOverlay, frameUrl]);

  useEffect(() => {
    const onResize = () => drawOverlays();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const latestEvents = useMemo(() => (Array.isArray(events) ? events.slice(0, 10) : []), [events]);
  const latestViolations = useMemo(
    () => (Array.isArray(violations) ? violations.slice(0, 10) : []),
    [violations]
  );

  const resolveViolation = async sv_id => {
    const id = safeText(sv_id).trim();
    if (!id) return;
    setResolvingViolationIds(prev => new Set(prev).add(id));
    try {
      await videoAttendanceApi.resolveSafetyViolation({ sv_id: id });
      setViolations(prev => (Array.isArray(prev) ? prev.filter(v => safeText(v?.sv_id || v?.id) !== id) : []));
    } catch (e) {
      setLoadError(e?.message || 'Failed to resolve violation.');
    } finally {
      setResolvingViolationIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <div className="w-full min-h-full flex flex-col gap-4 rounded-xl">
      <section className="px-2 sm:px-3 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-white/60 bg-white/60 px-3 py-2 text-sm font-semibold text-neutral-800 hover:bg-white/80"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <div>
            <div className="text-sm font-semibold text-neutral-900">
              Live View
              {cameraId ? <span className="text-neutral-600 font-medium"> • {cameraId}</span> : null}
            </div>
            <div className="text-xs text-neutral-600">
              {siteId ? `Site: ${siteId}` : 'Site: —'} • Date: {date}
              {frameLastUpdatedAt ? ` • Last updated: ${frameLastUpdatedAt}` : ''}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => setPaused(p => !p)}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white !text-white px-3 py-2 text-sm font-semibold hover:bg-emerald-700"
          >
            {paused ? <Play className="w-4 h-4 text-white" /> : <Pause className="w-4 h-4 text-white" />}
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button
            type="button"
            onClick={() => {
              const ac = new AbortController();
              fetchFrame(ac.signal);
              fetchFeeds(ac.signal);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-white/60 bg-white/70 px-3 py-2 text-sm font-semibold text-neutral-900 hover:bg-white"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0 px-2 sm:px-3 pb-3">
        <div className="lg:col-span-8 min-h-0 flex flex-col gap-3">
          <div className="rounded-xl border border-white/60 bg-white/55 p-3 flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-neutral-800">
              <input type="checkbox" checked={showZones} onChange={e => setShowZones(e.target.checked)} />
              Zones
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-neutral-800">
              <input
                type="checkbox"
                checked={showEventsOverlay}
                onChange={e => setShowEventsOverlay(e.target.checked)}
              />
              Events overlay
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-neutral-800">
              <input
                type="checkbox"
                checked={showViolationsOverlay}
                onChange={e => setShowViolationsOverlay(e.target.checked)}
              />
              Violations overlay
            </label>

            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <label className="text-xs text-neutral-600">
                Frame ms
                <input
                  type="number"
                  min={500}
                  step={100}
                  value={frameRefreshMs}
                  onChange={e => setFrameRefreshMs(Math.max(500, Number(e.target.value) || DEFAULT_FRAME_REFRESH_MS))}
                  className="ml-2 w-24 rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-800"
                />
              </label>
              <label className="text-xs text-neutral-600">
                Feeds ms
                <input
                  type="number"
                  min={1000}
                  step={500}
                  value={eventsRefreshMs}
                  onChange={e => setEventsRefreshMs(Math.max(1000, Number(e.target.value) || DEFAULT_EVENTS_REFRESH_MS))}
                  className="ml-2 w-24 rounded-lg border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-800"
                />
              </label>
            </div>
          </div>

          <div className="relative flex-1 min-h-[320px] rounded-xl overflow-hidden border border-white/60 bg-black/80">
            {frameUrl ? (
              <>
                <img
                  ref={imgRef}
                  src={frameUrl}
                  alt="Latest frame"
                  className="absolute inset-0 w-full h-full object-contain"
                  onLoad={() => drawOverlays()}
                  onError={() => setFrameError('Failed to load frame image.')}
                />
                <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-sm text-white/80">No frame yet.</p>
              </div>
            )}
          </div>

          {frameError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {frameError}
            </div>
          ) : null}
          {loadError ? (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {loadError}
            </div>
          ) : null}
        </div>

        <aside className="lg:col-span-4 min-h-0 flex flex-col gap-3">
          <div className="rounded-xl border border-white/60 bg-white/55 overflow-hidden flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-white/60">
              <div className="text-sm font-semibold text-neutral-900">Recent events</div>
              <div className="text-xs text-neutral-600">Latest 10</div>
            </div>
            <div className="p-2 overflow-auto flex-1 min-h-0">
              {latestEvents.length === 0 ? (
                <p className="text-sm text-neutral-700 p-2">No events yet.</p>
              ) : (
                <div className="space-y-2">
                  {latestEvents.map(e => (
                    <div
                      key={safeText(e?.event_id || e?.id || `${e?.captured_at}-${e?.event_type}`)}
                      className="rounded-lg border border-white/60 bg-white/70 px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className="text-xs font-semibold"
                          style={{ color: eventColor(e?.event_type) }}
                        >
                          {safeText(e?.event_type)}
                        </span>
                        <span className="text-[11px] text-neutral-600">
                          {safeText(e?.captured_at)}
                        </span>
                      </div>
                      {e?.zone_id ? (
                        <div className="text-[11px] text-neutral-700 mt-1">
                          Zone: <span className="font-semibold">{safeText(e.zone_id)}</span>
                        </div>
                      ) : null}
                      {Array.isArray(e?.missing_gear) && e.missing_gear.length ? (
                        <div className="text-[11px] text-neutral-700 mt-1">
                          Missing: <span className="font-semibold">{e.missing_gear.join(', ')}</span>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-white/60 bg-white/55 overflow-hidden flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-white/60">
              <div className="text-sm font-semibold text-neutral-900">Open safety violations</div>
              <div className="text-xs text-neutral-600">Latest 10</div>
            </div>
            <div className="p-2 overflow-auto flex-1 min-h-0">
              {latestViolations.length === 0 ? (
                <p className="text-sm text-neutral-700 p-2">No open violations.</p>
              ) : (
                <div className="space-y-2">
                  {latestViolations.map(sv => (
                    (() => {
                      const key = safeText(sv?.sv_id || sv?.id || `${sv?.captured_at}-${sv?.violation_type}`);
                      const svId = safeText(sv?.sv_id || sv?.id);
                      const isResolving = svId ? resolvingViolationIds.has(svId) : false;
                      return (
                    <div
                      key={key}
                      className="rounded-lg border border-white/60 bg-white/70 px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-rose-700">
                          {safeText(sv?.violation_type)}
                        </span>
                        <span className="text-[11px] text-neutral-600">
                          {safeText(sv?.captured_at)}
                        </span>
                      </div>
                      {sv?.zone_id ? (
                        <div className="text-[11px] text-neutral-700 mt-1">
                          Zone: <span className="font-semibold">{safeText(sv.zone_id)}</span>
                        </div>
                      ) : null}
                      {sv?.status ? (
                        <div className="text-[11px] text-neutral-600 mt-1">
                          Status: <span className="font-semibold">{safeText(sv.status)}</span>
                        </div>
                      ) : null}
                      {svId ? (
                        <div className="mt-2 flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => resolveViolation(svId)}
                            disabled={isResolving}
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white !text-white px-3 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60"
                          >
                            {isResolving ? 'Resolving…' : 'Resolve'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
};

export default LiveViewScreen;

